import type { RiderMember, StageInput } from '@tunasong/schemas'

/**
 * The rider-level channel order (docs/ux/2026-10-16-rider-collaboration-ux.md, "Rider structure"). `inputOrder` holds
 * channel keys: an input's id is stable across Review updates, so a lead's order survives them. Rows the order does not
 * name (a member's new input, a new member) go after the ordered ones, in stage order.
 */

/** `${userId}:${inputId}`, or `${userId}:member` for a member without inputs (the legacy one-row shape) */
export const channelKey = (member: Pick<RiderMember, 'userId'>, input?: Pick<StageInput, 'id'>) =>
  `${member.userId}:${input ? input.id : 'member'}`

const named = (order: readonly string[] | undefined) => new Map((order ?? []).map((key, index) => [key, index]))

/** Rows the order names, in its order, then the rest in the order given (stage order) */
export const applyOrder = <T extends { key: string }>(rows: T[], order: readonly string[] | undefined) => {
  const positions = named(order)
  const ordered = rows
    .filter(row => positions.has(row.key))
    .sort((a, b) => (positions.get(a.key) ?? 0) - (positions.get(b.key) ?? 0))
  return [...ordered, ...rows.filter(row => !positions.has(row.key))]
}

/** Whether the lead's order applies to any current row */
export const hasCustomOrder = (rows: { key: string }[], order: readonly string[] | undefined) => {
  const positions = named(order)
  return rows.some(row => positions.has(row.key))
}

/** The order with `key` moved by `delta` places, clamped to the ends */
export const moveKey = (order: readonly string[], key: string, delta: number) => {
  const from = order.indexOf(key)
  if (from === -1) {
    return [...order]
  }
  const to = Math.min(Math.max(from + delta, 0), order.length - 1)
  const next = order.filter(k => k !== key)
  next.splice(to, 0, key)
  return next
}
